// utils/secureLogger.js
const isProduction = process.env.NODE_ENV === 'production';

/**
 * Masks email addresses for secure logging
 * @param {string} email - Email to mask
 * @returns {string} - Masked email
 */
export const maskEmail = (email) => {
  if (!email || typeof email !== 'string') return '[INVALID_EMAIL]';
  
  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) return '[INVALID_EMAIL]';
  
  if (localPart.length <= 2) {
    return `${localPart[0]}***@${domain}`;
  }
  
  return `${localPart.substring(0, 2)}***@${domain}`;
};

/**
 * Masks sensitive tokens for logging
 * @param {string} token - Token to mask
 * @returns {string} - Masked token info
 */
export const maskToken = (token) => {
  if (!token || typeof token !== 'string') return '[NO_TOKEN]';
  
  return `[TOKEN_LENGTH:${token.length}]`;
};

/**
 * Sanitizes response data for logging by removing sensitive information
 * @param {Object} data - Response data to sanitize
 * @returns {Object} - Sanitized data
 */
export const sanitizeResponseData = (data) => {
  if (!data || typeof data !== 'object') return data;
  
  const sanitized = { ...data };
  
  // Remove sensitive tokens
  if (sanitized.token) {
    sanitized.token = maskToken(sanitized.token);
  }
  
  if (sanitized.refreshToken) {
    sanitized.refreshToken = maskToken(sanitized.refreshToken);
  }
  
  if (sanitized.accessToken) {
    sanitized.accessToken = maskToken(sanitized.accessToken);
  }
  
  // For user data, only log minimal safe information
  if (sanitized.user) {
    sanitized.user = {
      id: sanitized.user._id || sanitized.user.id || '[ID_MASKED]',
      role: sanitized.user.role || 'user',
      isActive: sanitized.user.isActive || false,
      // Remove all PII
      email: '[EMAIL_MASKED]',
      name: '[NAME_MASKED]',
      phone: '[PHONE_MASKED]',
      address: '[ADDRESS_MASKED]'
    };
  }
  
  // Remove password fields entirely
  delete sanitized.password;
  delete sanitized.currentPassword;
  delete sanitized.newPassword;
  delete sanitized.confirmPassword;
  
  return sanitized;
};

/**
 * Secure console logging that respects production environment
 * @param {string} level - Log level (log, warn, error)
 * @param {string} message - Log message
 * @param {any} data - Optional data to log
 */
export const secureLog = (level, message, data = null) => {
  if (isProduction && level === 'log') {
    // In production, suppress regular logs, only allow warnings and errors
    return;
  }
  
  const sanitizedData = data ? sanitizeResponseData(data) : null;
  
  if (sanitizedData) {
    console[level](message, sanitizedData);
  } else {
    console[level](message);
  }
};

/**
 * Secure login attempt logger
 * @param {string} email - Email being used for login
 */
export const logLoginAttempt = (email) => {
  const maskedEmail = isProduction ? maskEmail(email) : email;
  secureLog('log', `🔄 Attempting unified login for: ${maskedEmail}`);
};

/**
 * Secure response logger
 * @param {string} operation - Operation being performed
 * @param {Object} response - Response data
 */
export const logResponse = (operation, response) => {
  secureLog('log', `📡 ${operation} response:`, response);
};

/**
 * Secure user data logger - logs only essential non-PII information
 * @param {Object} user - User data to log
 */
export const logUserData = (user) => {
  // Only log essential authentication status, never PII
  if (isProduction) {
    secureLog('log', '✅ User authenticated via httpOnly cookies');
  } else {
    const safeData = {
      id: user?._id ? '[ID_PROVIDED]' : '[NO_ID]',
      role: user?.role || 'user',
      isActive: user?.isActive || false,
      hasEmail: !!user?.email,
      hasName: !!user?.name
    };
    secureLog('log', '✅ User verified with server via httpOnly cookies:', safeData);
  }
};